import { NextPage } from 'next';
import { NextSeo } from 'next-seo';

import React, { Fragment, useState } from 'react';

import { AnyArray } from 'immer/dist/internal';

import { DOMAIN } from '@common/const';
import { alertError } from '@common/modal';
import { css } from '@emotion/react';
import { SearchType } from '@type/web/event';

import useShareDataPopUp from '@lib/hook/share/useShareDataPopUp';

import useRouterHook from '@hook/useRouterHook';

import EventList from '@components/web/Event/EventList/EventList';
import EventNav from '@components/web/Event/EventList/EventNav/EventNav';
import useInfiniteQueryWithScroll from '@components/web/Event/EventList/hooks/useInfiniteQueryWithScroll';
import BasicLayout from '@components/web/Layout/BaisicLayout';
import IconLoading from '@components/web/common/icons/IconLoading';

const isSearchType = (target: string | undefined): boolean => {
  if (!target) return false;
  return target in SearchType;
};

const EventListPage: NextPage = () => {
  const { router, replace, back, queryString } = useRouterHook();
  const searchType = (queryString?.searchType as SearchType) || SearchType.ING;

  /** params가 SearchType 아닌 경우 리다이렉트 */
  if (!isSearchType(searchType)) {
    replace('/event/list/ING');
  }

  /** InfiniteScroll을 위한 data와 관찰 요소 */
  const { data, error, isFetching, ObservationComponent } =
    useInfiniteQueryWithScroll();

  if (error) {
    alertError({
      onOk: () => back(),
    });
  }

  const eventList = data?.reduce((acc, { result }): AnyArray => {
    return [...acc, ...result];
  }, []);

  /** 공유하기 */
  const [shareData, setShareData] = useState({
    bannerUrl: '',
    bannerTitle: '',
    shareUrl: '/event/list',
    description: '이벤트 상세',
    title: '[중고나라] 이벤트',
    text: '이벤트 상세',
  });
  const { SharePopUpComponent, onShowPopUp } = useShareDataPopUp({
    defaultData: {
      type: '이벤트 상세',
      text: '중고나라 이벤트',
      kakao: {
        title: '중고나라 이벤트',
        btn_title: '지금 보러가기',
      },
    },
    data: shareData,
    shareMessage: '친구들에게 이벤트 공유하기!',
  });

  const EventListPageSeo = () => (
    <NextSeo
      title="이벤트 리스트"
      description="중고나라 이벤트 리스트"
      canonical={`${DOMAIN}${router.asPath}`}
      openGraph={{
        type: 'website',
        locale: 'ko_KR',
        url: `${DOMAIN}${router.asPath}`,
        site_name: `중고나라`,
        title: '이벤트 리스트 | 중고나라',
        description: '중고나라 이벤트 리스트',
      }}
    />
  );

  return (
    <Fragment>
      <EventListPageSeo />
      <BasicLayout
        headerOptions={{
          isShowBackground: true,
          types: [
            {
              type: 'primary',
              response: {
                rightButtons: [{ type: 'appDownload' }, { type: 'category' }],
              },
            },
            { type: 'navigation' },
            {
              type: 'custom',
              response: () => <EventNav searchType={searchType} />,
            },
          ],
        }}
      >
        <div
          css={css`
            max-width: 1200px;
            margin: 0 auto;
          `}
        >
          <EventList
            onShowPopUp={onShowPopUp}
            setShareData={setShareData}
            eventList={eventList}
          />
          {isFetching && <IconLoading type="SpaceLayout" />}
          <ObservationComponent />
        </div>
        <SharePopUpComponent />
      </BasicLayout>
    </Fragment>
  );
};

export default EventListPage;
